import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'USAFlightFare',
  url: 'https://usaflightfare.com',
  description: 'USAFlightFare is a third-party travel booking platform that helps travelers find and book affordable domestic and international flights with full transparency and 24/7 support.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-888-509-8604',
    email: 'support@usaflightfare.com',
    contactType: 'customer support',
    availableLanguage: 'English',
  },
};

const STATS = [
  { value: '500+', label: 'Airlines Covered' },
  { value: '1,000+', label: 'Routes Available' },
  { value: '24/7', label: 'Live Support' },
  { value: '100%', label: 'Transparent Pricing' },
];

const VALUES = [
  { icon: '🔍', title: 'Full Transparency', desc: 'We show the complete fare — including taxes and fees — before you commit. No last-minute surprises at checkout.' },
  { icon: '🎧', title: '24/7 Live Support', desc: 'Real agents available around the clock. Whether it\'s a booking question or an urgent change, we\'re always one call away.' },
  { icon: '💰', title: 'Competitive Fares', desc: 'We compare rates across hundreds of airlines and routes to help you find affordable options for any trip.' },
  { icon: '✈️', title: 'All Trip Types', desc: 'From one-way domestic hops to multi-city international itineraries — we assist with every kind of flight booking.' },
  { icon: '🔄', title: 'Flexible Changes', desc: 'Need to adjust your plans? Our team helps with date changes, cancellations, and rebooking based on airline policies.' },
  { icon: '🛡️', title: 'Secure Booking', desc: 'Every booking is processed through secure, encrypted systems. Your personal and payment information is always protected.' },
];

export default function AboutPage() {
  return (
    <div>
      <SEO
        title="About Us | USAFlightFare"
        canonical="/about"
        description="USAFlightFare is a third-party travel platform helping travelers access transparent, affordable flight booking with 24/7 live support. Learn more about who we are."
        keywords="about usaflightfare, flight booking platform, travel assistance, transparent fares, 24/7 flight support"
        jsonLd={aboutJsonLd}
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 text-white py-16 sm:py-24 px-4 text-center">
        <h1 className="text-3xl sm:text-5xl font-extrabold mb-5 leading-tight">About USAFlightFare</h1>
        <p className="text-green-100 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
          A third-party travel platform built around one idea — helping travelers book flights with full transparency, no hidden fees, and real human support.
        </p>
      </div>

      {/* Stats */}
      <div className="bg-white py-10 sm:py-14 px-4 border-b border-gray-100">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map(s => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-extrabold text-emerald-600">{s.value}</p>
              <p className="text-gray-500 text-sm mt-1 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Who We Are */}
      <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20">
        <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
          <div>
            <p className="text-emerald-600 font-semibold text-sm uppercase tracking-widest mb-2">Who We Are</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-5 leading-tight">About USAFlightFare</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              USAFlightFare was created with the goal of helping travelers access professional travel assistance and clear booking information. We understand that every trip is unique, and we strive to support customers throughout their travel planning journey.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our commitment is to provide transparent communication, reliable customer service, and a straightforward booking experience. We encourage travelers to review all applicable travel terms, airline conditions, and policy information before completing any reservation.
            </p>
            <p className="text-gray-600 leading-relaxed">
              For additional information, please visit our <a href="/about" className="text-emerald-600 hover:underline">About Us</a>, <a href="/contact" className="text-emerald-600 hover:underline">Contact Us</a>, <a href="/privacy-policy" className="text-emerald-600 hover:underline">Privacy Policy</a>, <a href="/terms" className="text-emerald-600 hover:underline">Terms &amp; Conditions</a>, <a href="/refund-policy" className="text-emerald-600 hover:underline">Refund Policy</a>, and <a href="/service-fees" className="text-emerald-600 hover:underline">Service Fees</a> pages.
            </p>
          </div>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
              <p className="text-emerald-800 font-bold text-lg mb-1">Our Mission</p>
              <p className="text-emerald-700 text-sm leading-relaxed">
                To make flight booking transparent, accessible, and stress-free — with honest pricing and human support at every step.
              </p>
            </div>
            <div className="bg-teal-50 border border-teal-100 rounded-2xl p-6">
              <p className="text-teal-800 font-bold text-lg mb-1">Our Commitment</p>
              <p className="text-teal-700 text-sm leading-relaxed">
                We encourage all travelers to review applicable airline terms, fare conditions, and policies before completing a reservation. We are here to help you make informed decisions.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
              <p className="text-gray-800 font-bold text-lg mb-1">Important Note</p>
              <p className="text-gray-600 text-sm leading-relaxed">
                USAFlightFare is an independent travel support service. We are not affiliated with, endorsed by, or acting as a representative of any airline.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer */}
      <div className="bg-gray-50 py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-emerald-600 font-semibold text-sm uppercase tracking-widest mb-2">What We Offer</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Why Travelers Choose USAFlightFare</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map(v => (
              <div key={v.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="text-3xl mb-3">{v.icon}</div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20">
        <div className="text-center mb-10">
          <p className="text-emerald-600 font-semibold text-sm uppercase tracking-widest mb-2">Get in Touch</p>
          <h2 className="text-3xl font-extrabold text-gray-900">Contact Information</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: '📞', label: 'Phone', value: '+1 (888) 509-8604', href: 'tel:+18885098604' },
            { icon: '📧', label: 'Email', value: 'support@usaflightfare.com', href: 'mailto:support@usaflightfare.com' },
            { icon: '🕐', label: 'Hours', value: '24 / 7 — All Days', href: null },
          ].map(item => (
            <div key={item.label} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <div className="text-3xl mb-3">{item.icon}</div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">{item.label}</p>
              {item.href ? (
                <a href={item.href} className="text-gray-800 font-semibold hover:text-emerald-600 transition-colors">{item.value}</a>
              ) : (
                <p className="text-gray-800 font-semibold">{item.value}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Useful Links */}
      <div className="bg-emerald-900 text-white py-12 px-4 text-center">
        <h3 className="text-2xl font-extrabold mb-3">Helpful Pages</h3>
        <p className="text-green-200 mb-7 text-sm">Everything you need to know before, during, and after your trip.</p>
        <div className="flex flex-wrap justify-center gap-3 text-sm font-semibold">
          {[
            { to: '/contact', label: 'Contact Us' },
            { to: '/privacy-policy', label: 'Privacy Policy' },
            { to: '/terms', label: 'Terms & Conditions' },
            { to: '/refund-policy', label: 'Refund Policy' },
            { to: '/service-fees', label: 'Service Fees' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-full transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="py-14 px-4 text-center bg-white">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">Ready to find your flight?</h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">Search hundreds of airlines and routes — transparent pricing, no hidden fees.</p>
        <Link to="/" className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-10 py-3.5 rounded-xl transition-colors text-lg shadow">
          Search Flights →
        </Link>
      </div>
    </div>
  );
}
